export interface DataSource {
    id: number;
    name: string;
    alias: string;
    tenant_id: string;
    owners: string[];
    visibility: string;
    index?: string;
    description?: string;
    chat_session_id?: string;
    count: number;
    rag_type?: string;
}

export interface SavedPrompt {
    id: number;
    name: string;
    content: string;
    visibility: string;
    tenant_id: string;
    author: string;
    import_count: number;
    usage_count: number;
    catalog_prompt_id?: number;
    description?: string;
    order?: number;
    skill_name?: string;
    tags?: Tag[];
}

export interface Tag {
    id: number;
    name: string;
    order: number;
    tenant_id: string;
}

export type OrganizationPrompt = {
    tenant_id: string;
    content: string;
};

export type UserPrompt = {
    prompt: SavedPrompt;
    order?: number;
};

export type CatalogPrompt = {
    id: number;
    tenant_id: string;
    prompt: SavedPrompt;
    tags?: Tag[];
    import_count: number;
    visibility: string;
};

export interface ChatSession {
    id: string;
    name: string;
    is_active: boolean;
    first_visit: boolean;
    updated_at?: string;
    document_id?: string;
    custom_agent_id?: string;
    project_id?: string | null;
    agent_version?: number | null;
}

export interface Project {
    id: string;
    author_id: string;
    name: string;
    description?: string | null;
    instructions?: string | null;
    visibility: string;
    created_at: string;
    updated_at: string;
}

/**
 * A session listed in a project. `document_type` is resolved server-side so the row can show the
 * same slides/document distinction as the sidebar; null for a plain chat.
 */
export interface ProjectSession {
    id: string;
    name: string;
    document_id?: string | null;
    document_type?: string | null;
}

/**
 * One thing the assistant recorded about a project, and the conversation it recorded it in.
 *
 * The file is the assistant's own: the user reads it, or drops a line from it, but never writes
 * to it — a document mixing the two could no longer say which is which. `source_session_id` is
 * null for a line belonging to no conversation, and is what makes a wrong line correctable.
 */
export interface MemoryLine {
    text: string;
    source_session_id?: string | null;
}

/**
 * A project's documents, in the same shape as any data source's — `indexing_status` on each is
 * what makes the wait legible: until it is INDEXED, the document is attached but not yet findable.
 *
 * Every data source the project searches at once, its own and the associated ones undistinguished;
 * each document says which one it came from through its `data_source_id`.
 */
export interface ProjectDocuments {
    /** The project's own data source: null until the first upload, and where the next one goes. */
    data_source_id: number | null;
    documents: Item[];
    /** Where the next page starts, null once there is none — what the panel pages on. */
    next_offset: number | null;
}

/**
 * The documents of one of the data sources a project searches.
 *
 * The split is what the panel offers an action on: the project's own documents can be deleted one
 * by one, an associated data source can only be dropped whole — deleting out of a source the
 * project merely searches is not the project's to do.
 */
export interface ProjectDocumentGroup {
    dataSource: DataSource;
    isOwn: boolean;
    documents: Item[];
}

export interface ChatSessionSearchResultItem {
    chat_session: ChatSession;
    snippet: string | null;
}

export interface ChatSessionSearchResponse {
    items: ChatSessionSearchResultItem[];
    has_more: boolean;
}

export interface CanvasSession {
    id: string;
    title: string;
    content: string;
    type: string;
    updated_at: string;
    created_at: string;
    referrer_id?: string;
}

export interface DocumentItem {
    id: string;
    name: string;
    document_type: string;
    updated_at: string;
    created_at: string;
    content?: string;
    referrer_id?: string;
    theme_id?: string | null;
}

export interface UserDocumentTab {
    id: string;
    user_id: string;
    document_id: string;
    document_name: string;
    document_type: string;
    created_at: string;
    updated_at: string;
}

export interface DocumentVersion {
    id: string;
    document_id: string;
    content: string;
    created_at: string;
    updated_at: string;
}

export enum CustomAgentSourceType {
    DATA_SOURCE = 'data_source',
    ATTACHED_FILE = 'attached_file'
}

export interface CustomAgentSource {
    id: number;
    source_type: CustomAgentSourceType;
    data_source_id?: number;
    datasource_item_id?: string;
    // Full objects for frontend display/editing
    item?: Item;
    data_source?: DataSource;
}

export interface CustomAgentSourcesData {
    datasources: DataSource[];
    attached_files: Item[];
}

export interface CustomAgent {
    id: string;
    tenant_id: string;
    name: string;
    author_id: string;
    is_web_mode: boolean;
    instructions: string;
    created_at: string;
    updated_at: string;
    description?: string;
    icon?: string;
    color?: string;
    order?: number;
    use_count?: number;
    visibility?: Visibility;
    sources?: CustomAgentSource[];
    source_datas?: CustomAgentSourcesData;
    role?: Role;
    ai_model_id?: string;
}

export interface Message {
    content: string;
    type: string;
    isLoading: boolean;
    sources?: Sources; // todo - define type
    fileName?: string;
}

export type Sources = {
    images?: ImageMetadata[];
    memory?: ChatMessage[];
    summaries?: Summary[];
    documents?: Document[];
    web_infos?: WebInfo[];
};

export type ChatMessage = {
    content: string;
    type: string;
};

export type BaseSourceType = {
    content: string;
    metadata: any;
};

export type WebInfo = BaseSourceType;
export type Document = BaseSourceType;
export type Summary = BaseSourceType;

export type ImageMetadata = {
    image_id: string;
    source: string;
    web_url?: string;
    page?: number;
};

export type MessageImage = {
    image: string;
    metadata: ImageMetadata;
};

export enum IndexingStatus {
    PENDING = 'PENDING',
    PROCESSING = 'PROCESSING',
    INDEXED = 'INDEXED',
    ERROR = 'ERROR'
}

export interface Item {
    id: string;
    name: string;
    item_type: string;
    indexing_status?: IndexingStatus;
    site_id?: string;
    drive_id?: string;
    drive_item_id?: string;
    active?: boolean;
    datasource_item_id?: string;
    /** Which data source the item was listed from, when several were asked for at once. */
    data_source_id?: number;
    error?: string;
    created_at?: string;
    /** SharePoint `lastModifiedDateTime`. Absent on manual uploads and on files not re-indexed since sync. */
    last_modified_at?: string;
    web_url?: string;
    storage_path?: string;
    type?: string;
}

export type SearchItem = {
    id: string;
    name: string;
    type: string;
    folder: boolean;
    webUrl: string;
    parentReference: {
        driveId: string;
        id: string;
        siteId: string;
        sharepointIds: any;
    };
};

export interface Folder {
    site_id?: string;
    drive_id?: string;
    drive_item_id?: string;
    items: Item[];
}

export enum Role {
    OWNER = 'owner',
    READER = 'reader',
    NONE = 'none'
}

/** Someone a resource is shared with. */
export interface UserPermission {
    user_id: string;
    role: Role;
    name: string | null;
    email: string | null;
}

/** The permission calls of one shared resource, for `UserPermissions`. */
export interface PermissionsApi {
    list: () => Promise<UserPermission[]>;
    add: (email: string, role: Role) => Promise<UserPermission>;
    remove: (userId: string) => Promise<void>;
}

export enum Visibility {
    PRIVATE = 'private',
    PUBLIC = 'public',
    SHARED = 'shared',
    GLOBAL = 'global'
}

export enum DataSourceRAGType {
    SIMPLE_SHAREPOINT_RAG = 'Simple SharePoint search',
    ADVANCED_RAG = 'Advanced search'
}

export type OnboardingType =
    | 'app_onboarding'
    | 'chat'
    | 'datasource_creation'
    | 'datasource_upload'
    | 'saved_prompt_creation';

export type OnboardingStatus = 'pending' | 'done' | 'skipped';

export type AppOnboardingStep = {
    step: number;
    name: string;
};

export interface Onboarding {
    id: number;
    user_id: string;
    tenant_id: string;
    type: OnboardingType;
    status?: OnboardingStatus;
    current_step?: number;
    skipped_theme_step?: boolean | null;
    created_at: string;
    updated_at: string;
}

export interface Skill {
    name: string;
    description: string;
}

export type Notification = {
    id: string;
    user_id: string;
    tenant_id: string;
    message: string;
    unread: boolean;
    is_archived: boolean;
    created_at: Date;
    updated_at: Date;
};

export enum Skills {
    CHAT_WITH_DOCUMENTS = 'Chat with documents',
    CHAT_WITH_LLM = 'Chat with ChatGPT',
    CHAT_WITH_WEB = 'Web search',
    NONE = 'None'
}

export enum GenerationMode {
    DEFAULT = 'default',
    REASONING = 'reasoning',
    DEEP_RESEARCH = 'deep_research',
    PPTX_AGENT = 'pptx_agent',
    RAG_AGENT = 'rag_agent',
    SLIDE_STUDIO_AGENT = 'slide_studio_agent',
    DOC_STUDIO_AGENT = 'doc_studio_agent'
}

export type PromptAnswer = {
    content: string;
    completed: boolean;
};

export type PromptAnswers = {
    [key: string]: PromptAnswer;
};

export interface PptxLayout {
    layout_id: string;
    name: string;
    preview?: string; // e.g. a base64 encoded image or a URL to the preview
    source: string;
}

export interface PptxSlidePreview {
    slide_id: string;
    preview: string; // e.g. a base64 encoded image or a URL to the preview
}

export interface PptxPresentation {
    presentation_id: string;
    output_path: string;
    slides: SlideData[];
}

export interface ReleaseNote {
    id: number;
    tenant_id: string;
    date_version: string;
    content: string;
    created_at: Date;
    updated_at: Date;
    images: string[];
}

export interface SlideData {
    message: string;
    slide_id: string;
    preview: string;
    instruction?: string;
}

export interface VersionInfo {
    can_undo: boolean;
    can_redo: boolean;
}

export interface Slide {
    slideNumber: number;
    slideUrl: string | null;
    state: 'loading' | 'loaded' | 'error' | 'empty';
    mask: string | null;
    showMask: boolean;
    previousSlideUrl: string | null;
    showPreviousSlideUrl: boolean;
}

export interface OriginalTemplateSlide {
    filename: string;
    data: string;
}
export interface SlideSearchResult {
    doc_id: string;
    image_id: string;
    slide_number: number;
    doc_name: string;
    file_path: string;
    image: string;
    item_id: string;
}

export type Token = {
    accessToken: string | undefined;
    fromTeams: boolean;
};

export type SlideStudioPageItem = {
    id: string;
    page_number: number;
    content: string | null;
    error?: boolean;
};

export type DocumentSession = {
    chatSession: ChatSession;
    document: DocumentItem;
};

export enum SubscriptionPlan {
    TRIAL = 'trial',
    PRO = 'pro',
    ENTERPRISE = 'enterprise'
}

export interface UserModelSpending {
    tenant_id: string;
    user_id: string;
    period_start: string;
    period_end: string;
    used_usd: number;
    limit_usd: number;
    billing_provider: string;
    created_at: string;
    updated_at: string;
}
